/**
 * RevokeConfirmDialog
 * ===================
 * TREATX-SECURE-MEDICAL-FILE-SHARING-10B-UX-ACCEPTANCE-FIX
 *
 * Styled confirmation dialog for revoking a clinic's access to a
 * medical file. Replaces the native `window.confirm()` so the patient
 * sees clear, plain-language consequences before clicking "Stop
 * sharing".
 *
 * Locked owner-UX rules:
 *  - Recipient is shown by NAME (never an ID).
 *  - Wording reassures the patient that:
 *      1. the file stays in their vault,
 *      2. the audit history is preserved,
 *      3. the clinic loses access immediately.
 */
import React from 'react';
import { ShieldOff, X, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';

const RevokeConfirmDialog = ({
  open,
  clinicName,
  fileName,
  submitting,
  onConfirm,
  onClose,
}) => {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 z-[80] flex items-center justify-center p-4"
      data-testid="revoke-confirm-dialog"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-5 py-4 border-b">
          <div className="flex items-start gap-2.5">
            <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center shrink-0">
              <ShieldOff className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900" data-testid="revoke-confirm-title">
                Stop sharing this file?
              </h3>
              <p className="text-[11px] text-slate-500 mt-0.5">
                The clinic loses access immediately.
              </p>
            </div>
          </div>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose} data-testid="revoke-confirm-close-btn">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="px-5 py-4 space-y-3 text-sm text-slate-700">
          {fileName && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs">
              <span className="text-slate-500">File:&nbsp;</span>
              <span className="font-medium text-slate-900" data-testid="revoke-confirm-file-name">
                {fileName}
              </span>
            </div>
          )}
          <p>
            This will block future access for{' '}
            <span className="font-semibold text-slate-900" data-testid="revoke-confirm-clinic-name">
              {clinicName || 'this clinic'}
            </span>.
          </p>
          <ul className="text-xs text-slate-600 space-y-1.5 list-none pl-0">
            <li className="flex items-start gap-1.5">
              <span className="text-emerald-600 mt-0.5">•</span>
              <span>The file stays in your Medical Files vault.</span>
            </li>
            <li className="flex items-start gap-1.5">
              <span className="text-emerald-600 mt-0.5">•</span>
              <span>The audit history is preserved.</span>
            </li>
            <li className="flex items-start gap-1.5">
              <span className="text-emerald-600 mt-0.5">•</span>
              <span>You can share it again at any time.</span>
            </li>
          </ul>
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-900">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-600" />
            <span>Already-downloaded copies on the clinic side are out of your control.</span>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 bg-slate-50 border-t">
          <Button variant="outline" onClick={onClose} data-testid="revoke-confirm-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={submitting}
            className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
            data-testid="revoke-confirm-submit-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                Stopping…
              </>
            ) : (
              <>
                <ShieldOff className="h-4 w-4 mr-1.5" />
                Stop sharing
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RevokeConfirmDialog;
